
import React, { useState, useEffect } from 'react';
import { Card, CardContent, Typography, Box, Chip, Button, Skeleton, Stack } from '@mui/material';
import { AccessTime, OpenInNew, Newspaper } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';

const NewsWidget = () => {
    const theme = useTheme();
    const [news, setNews] = useState([]);
    const [loading, setLoading] = useState(true);

    // Placeholder data - In a real app, fetch from an API like NewsAPI.org
    // Example endpoint: https://newsapi.org/v2/top-headlines?category=business&apiKey=YOUR_KEY
    useEffect(() => {
        const fetchNews = async () => {
            // Simulate API delay
            setTimeout(() => {
                setNews([
                    {
                        id: 1,
                        title: "Global Markets Rally as Inflation Data Cools Down",
                        source: "Financial Times",
                        time: "2h ago",
                        category: "Markets",
                        url: "#",
                        image: "https://images.unsplash.com/photo-1611974765270-ca1258634369?auto=format&fit=crop&q=80&w=200"
                    },
                    {
                        id: 2,
                        title: "Tech Giants Announce New AI Partnerships for 2026",
                        source: "TechCrunch",
                        time: "4h ago",
                        category: "Technology",
                        url: "#",
                        image: "https://images.unsplash.com/photo-1518770660439-4636190af475?auto=format&fit=crop&q=80&w=200"
                    },
                    {
                        id: 3,
                        title: "Company Quarterly Results Exceed Expectations",
                        source: "Internal",
                        time: "1d ago",
                        category: "Company",
                        url: "#",
                        image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?auto=format&fit=crop&q=80&w=200"
                    }
                ]);
                setLoading(false);
            }, 1500);
        };

        fetchNews();
    }, []);

    return (
        <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ p: 3, flexGrow: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Newspaper color="primary" />
                        <Typography variant="h6" color="text.primary">Latest News</Typography>
                    </Box>
                    <Button size="small" endIcon={<OpenInNew />} sx={{ color: theme.palette.primary.main }}>
                        View All
                    </Button>
                </Box>

                <Stack spacing={3}>
                    {loading ? (
                        // Skeleton Loading State
                        [1, 2, 3].map((item) => (
                            <Box key={item} sx={{ display: 'flex', gap: 2 }}>
                                <Skeleton variant="rounded" width={80} height={80} sx={{ borderRadius: 2 }} />
                                <Box sx={{ flex: 1 }}>
                                    <Skeleton variant="text" width="60%" />
                                    <Skeleton variant="text" width="90%" />
                                    <Skeleton variant="text" width="40%" />
                                </Box>
                            </Box>
                        ))
                    ) : (
                        // News Items
                        news.map((item) => (
                            <Box
                                key={item.id}
                                sx={{
                                    display: 'flex',
                                    gap: 2,
                                    cursor: 'pointer',
                                    transition: 'transform 0.2s ease',
                                    '&:hover': {
                                        transform: 'translateX(5px)',
                                        '& .news-title': { color: theme.palette.primary.main }
                                    }
                                }}
                                onClick={() => window.open(item.url, '_blank')}
                            >
                                <Box
                                    component="img"
                                    src={item.image}
                                    alt={item.title}
                                    sx={{
                                        width: 80,
                                        height: 80,
                                        borderRadius: 3,
                                        objectFit: 'cover',
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                                    }}
                                />
                                <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
                                        <Chip
                                            label={item.category}
                                            size="small"
                                            sx={{
                                                height: 20,
                                                fontSize: '0.65rem',
                                                fontWeight: 700,
                                                bgcolor: alpha(theme.palette.primary.main, 0.1),
                                                color: theme.palette.primary.main
                                            }}
                                        />
                                        <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <AccessTime fontSize="inherit" /> {item.time}
                                        </Typography>
                                    </Box>
                                    <Typography
                                        className="news-title"
                                        variant="subtitle2"
                                        sx={{
                                            lineHeight: 1.3,
                                            mb: 0.5,
                                            transition: 'color 0.2s ease',
                                            display: '-webkit-box',
                                            overflow: 'hidden',
                                            WebkitBoxOrient: 'vertical',
                                            WebkitLineClamp: 2
                                        }}
                                    >
                                        {item.title}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Source: {item.source}
                                    </Typography>
                                </Box>
                            </Box>
                        ))
                    )}
                </Stack>
            </CardContent>
        </Card>
    );
};

// Helper for alpha color since we aren't importing it from mui/material/styles in the component body
function alpha(color, opacity) {
    return color + Math.round(opacity * 255).toString(16).padStart(2, '0');
}

export default NewsWidget;
